import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Select,
  useDisclosure,
  useToast,
  HStack,
  VStack,
  Text,
  Spinner,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react';
import { FiPlus, FiEdit2, FiTrash2 } from 'react-icons/fi';
import {
  getMasterData,
  createCompanySubTask,
  updateCompanySubTask,
  deleteCompanySubTask,
} from '../../services/companyMasterDataAPI';

const CompanySubTaskManager = () => {
  const [categories, setCategories] = useState([]);
  const [activities, setActivities] = useState([]);
  const [subTasks, setSubTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubTask, setSelectedSubTask] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const cancelRef = React.useRef();
  const toast = useToast();

  // Form state
  const [formData, setFormData] = useState({
    masterActivityId: '',
    name: '',
    description: '',
    defaultProductivity: 0,
    unit: 'No',
    order: 0,
  });

  useEffect(() => {
    loadMasterData();
  }, []);

  const loadMasterData = async () => {
    try {
      setLoading(true);
      const response = await getMasterData();
      
      if (response.success) {
        setCategories(response.data || []);
        
        // Extract all activities and subtasks
        const allActivities = [];
        const allSubTasks = [];
        
        response.data.forEach((category) => {
          if (category.masterActivities) {
            category.masterActivities.forEach((activity) => {
              allActivities.push({
                ...activity,
                categoryName: category.name,
              });
              
              if (activity.masterSubTasks) {
                activity.masterSubTasks.forEach((subTask) => {
                  allSubTasks.push({
                    ...subTask,
                    activityName: activity.name,
                    activityCode: activity.code,
                    categoryName: category.name,
                  });
                });
              }
            });
          }
        });
        
        setActivities(allActivities);
        setSubTasks(allSubTasks);
      }
    } catch (error) {
      toast({
        title: 'Error loading master data',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (subTask = null) => {
    if (subTask) {
      // Edit mode
      setSelectedSubTask(subTask);
      setFormData({
        masterActivityId: subTask.masterActivityId,
        name: subTask.name,
        description: subTask.description || '',
        defaultProductivity: subTask.defaultProductivity || 0,
        unit: subTask.unit || 'No',
        order: subTask.order || 0,
      });
    } else {
      // Create mode
      setSelectedSubTask(null);
      setFormData({
        masterActivityId: '',
        name: '',
        description: '',
        defaultProductivity: 0,
        unit: 'No',
        order: 0,
      });
    }
    onOpen();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (selectedSubTask) {
        // Update existing subtask
        await updateCompanySubTask(selectedSubTask.id, formData);
        toast({
          title: 'SubTask updated',
          description: 'Company subtask updated successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      } else {
        // Create new subtask
        await createCompanySubTask(formData);
        toast({
          title: 'SubTask created',
          description: 'Company subtask created successfully',
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
      }
      
      onClose();
      loadMasterData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDeleteClick = (subTask) => {
    setDeleteTarget(subTask);
    onDeleteOpen();
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteCompanySubTask(deleteTarget.id);
      toast({
        title: 'SubTask deleted',
        description: 'Company subtask deleted successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      onDeleteClose();
      loadMasterData();
    } catch (error) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || error.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const isCompanySubTask = (subTask) => {
    return subTask.companyId !== null && subTask.companyId !== undefined;
  };

  if (loading) {
    return (
      <Box textAlign="center" py={10}>
        <Spinner size="xl" />
      </Box>
    );
  }

  return (
    <Box>
      <HStack justify="space-between" mb={4}>
        <VStack align="start" spacing={0}>
          <Text fontSize="xl" fontWeight="bold" color="white">
            SubTasks
          </Text>
          <Text fontSize="sm" color="gray.400">
            Manage your company's custom subtasks
          </Text>
        </VStack>
        <Button
          leftIcon={<FiPlus />}
          colorScheme="blue"
          onClick={() => handleOpenModal()}
        >
          Add SubTask
        </Button>
      </HStack>

      <Box
        bg="whiteAlpha.50"
        borderRadius="lg"
        border="1px solid"
        borderColor="whiteAlpha.200"
        overflow="hidden"
      >
        <Table variant="simple">
          <Thead bg="whiteAlpha.100">
            <Tr>
              <Th color="gray.300">Activity</Th>
              <Th color="gray.300">SubTask Name</Th>
              <Th color="gray.300">Productivity</Th>
              <Th color="gray.300">Unit</Th>
              <Th color="gray.300">Type</Th>
              <Th color="gray.300">Actions</Th>
            </Tr>
          </Thead>
          <Tbody>
            {subTasks.length === 0 ? (
              <Tr>
                <Td colSpan={6} textAlign="center" color="gray.400" py={8}>
                  No subtasks found. Click "Add SubTask" to create one.
                </Td>
              </Tr>
            ) : (
              subTasks.map((subTask) => (
                <Tr key={subTask.id} _hover={{ bg: 'whiteAlpha.50' }}>
                  <Td color="white">
                    <Text fontWeight="medium">{subTask.activityName}</Text>
                    <Text fontSize="xs" color="gray.400">
                      {subTask.activityCode} ({subTask.categoryName})
                    </Text>
                  </Td>
                  <Td color="white">{subTask.name}</Td>
                  <Td color="white">{subTask.defaultProductivity}</Td>
                  <Td color="white">{subTask.unit}</Td>
                  <Td>
                    {isCompanySubTask(subTask) ? (
                      <Badge colorScheme="purple" fontSize="xs">
                        Company
                      </Badge>
                    ) : (
                      <Badge colorScheme="gray" fontSize="xs">
                        Global
                      </Badge>
                    )}
                  </Td>
                  <Td>
                    {isCompanySubTask(subTask) ? (
                      <HStack spacing={2}>
                        <IconButton
                          aria-label="Edit subtask"
                          icon={<FiEdit2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="blue"
                          onClick={() => handleOpenModal(subTask)}
                        />
                        <IconButton
                          aria-label="Delete subtask"
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleDeleteClick(subTask)}
                        />
                      </HStack>
                    ) : (
                      <Text fontSize="xs" color="gray.500">
                        Read-only
                      </Text>
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </Box>

      {/* Add/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent bg="gray.800" color="white">
          <ModalHeader>
            {selectedSubTask ? 'Edit SubTask' : 'Add New SubTask'}
          </ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleSubmit}>
            <ModalBody>
              <VStack spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Activity</FormLabel>
                  <Select
                    value={formData.masterActivityId}
                    onChange={(e) =>
                      setFormData({ ...formData, masterActivityId: e.target.value })
                    }
                    bg="whiteAlpha.100"
                    disabled={!!selectedSubTask}
                  >
                    <option value="">Select an activity</option>
                    {activities.map((activity) => (
                      <option key={activity.id} value={activity.id}>
                        {activity.code} - {activity.name} ({activity.categoryName})
                      </option>
                    ))}
                  </Select>
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>SubTask Name</FormLabel>
                  <Input
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    placeholder="SubTask name"
                    bg="whiteAlpha.100"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Description</FormLabel>
                  <Textarea
                    value={formData.description}
                    onChange={(e) =>
                      setFormData({ ...formData, description: e.target.value })
                    }
                    placeholder="SubTask description (optional)"
                    bg="whiteAlpha.100"
                    rows={3}
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Default Productivity</FormLabel>
                  <Input
                    type="number"
                    step="0.000001"
                    value={formData.defaultProductivity}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        defaultProductivity: parseFloat(e.target.value) || 0,
                      })
                    }
                    placeholder="0.0"
                    bg="whiteAlpha.100"
                  />
                </FormControl>

                <FormControl isRequired>
                  <FormLabel>Unit</FormLabel>
                  <Input
                    value={formData.unit}
                    onChange={(e) =>
                      setFormData({ ...formData, unit: e.target.value })
                    }
                    placeholder="e.g., No, Sqm, Cum"
                    bg="whiteAlpha.100"
                  />
                </FormControl>

                <FormControl>
                  <FormLabel>Order</FormLabel>
                  <Input
                    type="number"
                    value={formData.order}
                    onChange={(e) =>
                      setFormData({ ...formData, order: parseInt(e.target.value) || 0 })
                    }
                    bg="whiteAlpha.100"
                  />
                </FormControl>
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={onClose}>
                Cancel
              </Button>
              <Button colorScheme="blue" type="submit">
                {selectedSubTask ? 'Update' : 'Create'}
              </Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800" color="white">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete SubTask
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete "{deleteTarget?.name}"? This action cannot
              be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default CompanySubTaskManager;
